'use client';

/* eslint-disable no-nested-ternary */

import { useCallback, useEffect, useState } from 'react';

import {
  IImageListItem,
  TImageTokenWithFile,
  TImageUploadStatus,
  TImagesDataById,
  useDeviceImagesListQuery,
  useTenantConfigQuery,
} from '@northladder/apis';
import { AppHeader, PublicFooter } from '@northladder/design-system';
import { IApiError } from '@northladder/utilities';

import {
  DevicesImagesListV2,
  InitiateAssessment,
  NoImagesLoadedYet,
  useRouteURL,
} from '~/features';

/**
 * -----------------------------------------------------------------------------
 * The landing screen of the app, showing all the capturing logic.
 * NOTE: This is a single page app.
 */
export default function Home() {
  const { sessionId, assessmentType, resetNavPath, platform } = useRouteURL();

  /**
   * This is a temporary work around to fix issues with the tenant config check
   * since multi-tenant is not yet deployed by the retail app in DEV.
   */
  const shouldSkipTenantConfigCheck = !platform;

  const {
    refetch: refetchConfig,
    isPending: isLoadingConfigBaseURL,
    error: configError,
    data: configData,
  } = useTenantConfigQuery({ identifier: platform });

  // TODO: @divyansh tech debt on region setup here.. use useTenantDetailsQuery
  const {
    data: imagesListData,
    error: imagesListError,
    isPending: isLoadingImagesList,
    fetchDevicesImagesList,
  } = useDeviceImagesListQuery({
    assessmentType,
    baseURL: configData?.apiBaseURL || '',
    sessionId,
  });

  useEffect(() => {
    if (configData?.apiBaseURL) {
      fetchDevicesImagesList();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configData?.apiBaseURL]);

  const [isDone, setIsDone] = useState(false);
  const [imagesDataIds, setImagesDataIds] = useState<string[]>([]);
  const [imagesDataById, setImagesDataById] = useState<TImagesDataById | null>(
    null
  );
  const [completedImages, setCompletedImages] = useState<IImageListItem[]>([]);
  const [pendingImages, setPendingImages] = useState<IImageListItem[]>([]);
  const [pendingReuploadImages, setPendingReuploadImages] = useState<
    IImageListItem[]
  >([]);

  const getUpdatedImagesList = useCallback(
    (newImagesData?: IImageListItem[]) => {
      const alreadyUploadedImages: IImageListItem[] = [];
      const pendingUploadImages: IImageListItem[] = [];
      const imagesPendingReupload: IImageListItem[] = [];

      newImagesData?.forEach((imageData) => {
        const imageStatus =
          imageData?.status?.toLowerCase() as TImageUploadStatus;

        if (imageStatus === 'completed') {
          alreadyUploadedImages.push(imageData);
        } else if (imageStatus === 're_upload') {
          imagesPendingReupload.push(imageData);
        } else {
          pendingUploadImages.push(imageData);
        }
      });

      const updatedImagesData: TImageTokenWithFile[] | undefined =
        newImagesData?.map((imageData) => ({
          ...imageData,
          croppedImgSrc: '',
          loadedImgSrc: '',
          status: imageData.status || 'pending',
        }));

      let updatedImagesDataById: TImagesDataById | null = null;
      const updatedImagesDataIds: string[] = [];

      if (updatedImagesData) {
        updatedImagesData.forEach((imageData) => {
          updatedImagesDataById = {
            ...(updatedImagesDataById || {}),
            [imageData.id]: imageData,
          };

          updatedImagesDataIds.push(imageData.id);
        });

        setCompletedImages(alreadyUploadedImages);
        setPendingImages(pendingUploadImages);
        setPendingReuploadImages(imagesPendingReupload);
        setImagesDataById(updatedImagesDataById);
        setImagesDataIds(updatedImagesDataIds);
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imagesListData]
  );

  useEffect(() => {
    if (!imagesListData) return;

    getUpdatedImagesList(imagesListData.images);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagesListData]);

  const handleRefetchImageList = () => {
    if (configError && !shouldSkipTenantConfigCheck) {
      refetchConfig();
    } else if (imagesListError) {
      fetchDevicesImagesList();
    }
  };

  const handleOnRestart = () => {
    resetNavPath();
  };

  const handleOnClickDone = () => {
    setIsDone(true);
    resetNavPath();

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const noImageListLoaded =
    !sessionId ||
    isDone ||
    isLoadingImagesList ||
    (isLoadingConfigBaseURL && !shouldSkipTenantConfigCheck) ||
    imagesListError ||
    !imagesDataById;

  return (
    <>
      <div className="flex min-h-screen flex-col overflow-hidden">
        <AppHeader />

        {noImageListLoaded ? (
          <NoImagesLoadedYet
            error={imagesListError as IApiError}
            hasNoImages={!imagesDataById}
            hasNoSessionId={!sessionId}
            isDone={isDone}
            isLoading={
              isLoadingImagesList ||
              (isLoadingConfigBaseURL && !shouldSkipTenantConfigCheck)
            }
            onRestart={handleOnRestart}
            onRetry={handleRefetchImageList}
          />
        ) : completedImages.length > 0 || assessmentType === 'GRADE_PRO' ? (
          <DevicesImagesListV2
            completedImages={completedImages}
            imagesDataById={imagesDataById}
            imagesDataIds={imagesDataIds}
            onClickDone={handleOnClickDone}
            pendingImages={pendingImages}
            pendingReuploadImages={pendingReuploadImages}
            productName={imagesListData?.productName}
          />
        ) : (
          <InitiateAssessment />
        )}
      </div>
      <PublicFooter showFooterNav={false} />
    </>
  );
}
